import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { BookingHeader } from 'components/bookingHeader';

import { Dropdown } from 'components/common/dropdown';

import { Room, SkeletonRoom } from 'components/room';

import { getBookingWidgetRoomData } from 'features/roomsSlice';
import { setCheckInDay, setCheckOutDay, setSortBy } from 'features/userSlice';

import { useGetBookingData, useGetRoomData, useScrollToTop } from 'hooks';

import './PropertyDetails.css';
import { SinglePropertySidebar } from 'components/singlePropertySidebar';
import { LowestRatesSlider } from 'components/lowestRatesSlider';
import { flatten } from 'lodash';
import { differenceInDays } from 'date-fns';
const PropertyDetails = ({
    closeProperyDetails,
    property = {},
    showHeader,
    openPropertyDetails,
}) => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.miniBookingWidget?.data);
    const multipropertyPropeties =
        useSelector((state) => state.multiproperty.multipropertyProperties) ||
        [];
    const selectedPropertyId = useSelector(
        (state) => state.multiproperty?.selectedPropertyId
    );
    const propertiesDropdown = multipropertyPropeties?.map((property) => {
        let option = {};

        if (selectedPropertyId === property.propertyID) {
            option = {
                dropdownText: property.propertyName,
                dropdownValue: property.propertyID,
                dropdownSelected: true,
            };
        } else {
            option = {
                dropdownText: property.propertyName,
                dropdownValue: property.propertyID,
                dropdownSelected: false,
            };
        }
        return option;
    });

    const sortByOptions = data?.bigPlugin?.sortByOptions;
    const sortByText = data?.bigPlugin?.sortByText;
    const roomsUrl = data?.bigPlugin?.allRoomsUrl;
    const Lang = useSelector((state) => state.miniBookingWidget?.data?.Lang);
    const getRoomData = useGetRoomData();
    const roomTypes = useSelector((state) => state.rooms.roomTypes);

    // TODO: CHANGE THIS
    const isFetchingPrices = useSelector(
        (state) => state.miniBookingWidget.isFetching
    );
    const isFetchingRooms = useSelector((state) => state.rooms?.isFetching);
    const isCartOpen = useSelector((state) => state.cart?.open);

    const ref = useScrollToTop([isFetchingRooms, isFetchingPrices]);

    useEffect(() => {
        if (roomsUrl) {
            dispatch(getBookingWidgetRoomData({ roomsUrl }));
        }
    }, [roomsUrl, isCartOpen]);

    const calendar = useSelector(
        (state) => state.miniBookingWidget?.data?.futureDates
    );

    const userCountryCode =
        useSelector((state) => state.user?.language) || 'en-US';
    const { getBookingData } = useGetBookingData();
    const onClick = (day) => {
        let checkOutDate = new Date(day?.date);
        const els = document.querySelectorAll('.ab-daily-lowest-rate');
        for (const element of els) {
            element.classList.remove('ab-day-selected');
        }
        const thisEl = document.querySelectorAll(`[data-date="${day?.date}"]`);
        thisEl[0].classList.add('ab-day-selected');
        checkOutDate = checkOutDate.setDate(
            checkOutDate.getDate() + day?.min_stay
        );
        checkOutDate = new Date(checkOutDate);
        const dateString = checkOutDate
            .toLocaleDateString('en-GB', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            })
            .split('/')
            .reverse()
            .join('-');

        const checkOutMonth = checkOutDate.getMonth() + 1;
        const checkOutMontObj = calendar.find((month) => {
            return month.fd_Month === checkOutMonth;
        });

        if (!checkOutMontObj) {
            console.error('Month not found');
            return false;
        }
        const checkOuDayObj = checkOutMontObj?.fd_JsonResult?.data?.days.find(
            (day) => {
                return day.date === dateString;
            }
        );
        if (!checkOuDayObj) {
            console.error('Day not found');
            return false;
        }
        dispatch(setCheckInDay({ day: day }));

        dispatch(setCheckOutDay({ day: checkOuDayObj }));

        getBookingData({
            checkInDate: day?.date,
            checkOutDate: checkOuDayObj?.date,
            propertyId: selectedPropertyId,
        });
        return false;
    };
    const initialDate = useSelector((state) => state.user?.initialDate);
    const [lowestRateDays, lowestRateNextDays, lowestRateMonthIndexes] =
        useMemo(() => {
            const filteredNextDays = [];
            const filteredMonthIndexes = [];
            const monthsToDisplay = calendar?.slice(0, 2);
            const filteredDays = monthsToDisplay.map((month, monthIndex) => {
                return month?.fd_JsonResult?.data?.days?.filter(
                    (day, dayIndex, daysArr) => {
                        const today = new Date(initialDate);
                        const thisDay = new Date(day?.date);
                        if (differenceInDays(thisDay, today) >= 0) {
                            const nextDay = daysArr[dayIndex + 1];

                            filteredMonthIndexes.push(monthIndex);
                            filteredNextDays.push(nextDay);
                            return day;
                        }
                        return false;
                    }
                );
            });
            return [
                flatten(filteredDays),
                filteredNextDays,
                filteredMonthIndexes,
            ];
        }, [calendar]);

    return (
        <div className="ab-page">
            {showHeader && (
                <BookingHeader onClick={closeProperyDetails}>
                    <Dropdown
                        label={sortByText}
                        onChange={(option) => {
                            const propertyId = option.dropdownValue;
                            const parentPropertyId = property?.parentPropertyID;
                            openPropertyDetails(propertyId, parentPropertyId);
                        }}
                        options={propertiesDropdown || []}
                        extraClass="ab-properties-dropdown"
                    />
                </BookingHeader>
            )}

            <div className="ab-body">
                <SinglePropertySidebar />

                <div className="ab-body-main" ref={ref}>
                    {data?.channelManager !== 'guesty' && (
                        <LowestRatesSlider
                            lowestRateDays={lowestRateDays}
                            lowestRateNextDays={lowestRateNextDays}
                            lowestRateMonthIndexes={lowestRateMonthIndexes}
                            Lang={Lang}
                            countryCode={userCountryCode}
                            onClick={onClick}
                            cellSpacing={10}
                            mainClass="ab-daily-lowest-rate"
                            title="LOWEST DAILY RATE"
                            wrapperClass="ab-lowest-daily-rates-slider-cnt ab-lowest-daily-desktop"
                            type="daily"
                            animation={true}
                        />
                    )}

                    <div className="ab-inner-filters-row">
                        <Dropdown
                            label={sortByText}
                            onChange={(option) => {
                                // TODO: NOT SURE ABOUT THIS
                                dispatch(
                                    setSortBy({
                                        sortBy: option.dropdownValue,
                                    })
                                );
                                getRoomData({
                                    sortBy: option.dropdownValue,
                                });
                            }}
                            options={sortByOptions || []}
                        />
                    </div>

                    <div
                        className="ab-rooms-container"
                        style={{ paddingBottom: '35px' }}
                    >
                        {(isFetchingRooms || isFetchingPrices) && (
                            <>
                                <SkeletonRoom />
                                <SkeletonRoom />
                                <SkeletonRoom />
                            </>
                        )}
                        {!isFetchingRooms &&
                            !isFetchingPrices &&
                            roomTypes.map((room, index) => {
                                return (
                                    <Room
                                        key={index}
                                        room={room}
                                        index={index}
                                        isBestRate={index === 0}
                                    />
                                );
                            })}
                    </div>
                </div>
            </div>
        </div>
    );
};

PropertyDetails.propTypes = {
    closeProperyDetails: PropTypes.func,
    property: PropTypes.shape({
        propertyName: PropTypes.string,
    }),
    showHeader: PropTypes.bool,
    openPropertyDetails: PropTypes.func,
};

export default PropertyDetails;
